// Basic
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
// Libs
import { useTranslation } from 'react-i18next';
// Components
import { PieChart, Pie, Cell } from 'recharts';
import { ChartContainer, ChartLegend, ChartTooltip, ChartTooltipContent } from '@/common-utils/components/ui/chart';
import { Separator } from '../../ui/separator';
import LegendMoreItemsTooltip from './LegendMoreItemsTooltip';
// Common
import getTranslatedText from '@/common-utils/functions/getTranslatedText';
import generateColorFromKey from '@/app/modules/dashboard/utils/generateColorFromKey';

/**
 * @param root0
 * @param root0.data
 * @param root0.dataKey
 * @param root0.chartKey
 * @param root0.valueKey
 * @param root0.namespace
 * @returns {JSX} PieChartCustom
 */
function PieChartCustom({ data, dataKey, chartKey, valueKey, namespace }) {
    // Hooks
    const { i18n } = useTranslation();

    // State
    const [chartData, setChartData] = useState([]);
    const [chartConfig, setChartConfig] = useState({});

    // Hooks
    useEffect(() => {
        if (data) {
            const rawBreakdown = data?.[dataKey];

            const dataValues = (Array.isArray(rawBreakdown) ? rawBreakdown : Object.values(rawBreakdown || []))
                ?.map((item) => ({
                    [chartKey]: item[chartKey],
                    [valueKey]: item[valueKey] ?? 0
                }))
                ?.filter((item) => item[chartKey]);

            const nextChartData = dataValues?.map(({ [chartKey]: chartKeyValue, [valueKey]: valueKeyValue }) => ({
                [chartKey]: chartKeyValue,
                [valueKey]: valueKeyValue,
                fill: generateColorFromKey(chartKeyValue)
            })
            )?.sort((a, b) => (Number(b?.[valueKey]) || 0) - (Number(a?.[valueKey]) || 0));

            setChartData(nextChartData);

            const nextChartConfig = {
                [valueKey]: {
                    label: getTranslatedText(valueKey, namespace)
                }
            };

            nextChartData?.forEach((item) => {
                nextChartConfig[item[chartKey]] = {
                    label: item[chartKey],
                    fill: item.fill
                };
            });

            setChartConfig(nextChartConfig);
        }
    }, [data]);

    return (
        <ChartContainer config={chartConfig} className='w-full max-h-[240px]'>
            <PieChart>
                <ChartLegend
                    layout='vertical'
                    verticalAlign='middle'
                    align='left'
                    content={(props) => {
                        const sortedPayload = [...(props.payload || [])].sort(
                            (a, b) =>
                                (Number(b?.payload?.[valueKey]) || 0) - (Number(a?.payload?.[valueKey]) || 0)
                        );

                        const maxLegendItems = 6;
                        const visibleItems = sortedPayload.slice(0, maxLegendItems);
                        const hiddenItems = sortedPayload.slice(maxLegendItems);

                        return (
                            <div className='flex-col items-start justify-center grid gap-4 pt-3'>
                                {visibleItems
                                    .filter((item) => item.type !== 'none')
                                    .map((item) => (
                                        <div
                                            key={item.value}
                                            className='[&>svg]:text-muted-foreground flex items-center gap-1.5 [&>svg]:h-3 [&>svg]:w-3'
                                        >
                                            <div
                                                className='h-2 w-2 shrink-0 rounded-[2px] space-x-2'
                                                style={{ backgroundColor: item.color }}
                                            />
                                            <span>
                                                {item.value}
                                            </span>
                                            <Separator orientation='vertical' className='h-4' />
                                            <span className='font-medium'>
                                                {item?.payload?.[valueKey]?.toLocaleString(i18n.language)}
                                            </span>
                                        </div>
                                    ))}
                                {!!hiddenItems.length && (
                                    <LegendMoreItemsTooltip items={hiddenItems} valueKey={valueKey} />
                                )}
                            </div>
                        );
                    }}
                    className='position-absolute left-0 bottom-0'
                />
                <ChartTooltip
                    isAnimationActive={false}
                    content={
                        <ChartTooltipContent
                            hideLabel
                            formatter={(value, _name, _item, _index, tooltipData) => (
                                <div className='flex w-full items-center justify-between gap-8 leading-none'>
                                    <p className='font-medium leading-snug'>
                                        {tooltipData?.[chartKey] || ''}
                                    </p>
                                    <span className='font-medium text-foreground tabular-nums'>
                                        {value !== null && value !== undefined && value !== ''
                                            ? Number(value).toLocaleString(i18n.language)
                                            : '—'}
                                    </span>
                                </div>
                            )}
                        />
                    }
                />
                <Pie
                    data={chartData}
                    dataKey={valueKey}
                    nameKey={chartKey}
                    style={{ paddingLeft: '50px' }}
                    innerRadius={50}
                    outerRadius={80}
                    strokeWidth={2}
                >
                    {chartData.map((entry) => (
                        <Cell key={entry[chartKey]} fill={entry.fill} />
                    ))}
                </Pie>
            </PieChart>
        </ChartContainer>
    );
}

PieChartCustom.propTypes = {
    data: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
    dataKey: PropTypes.string.isRequired,
    chartKey: PropTypes.string.isRequired,
    valueKey: PropTypes.string.isRequired,
    namespace: PropTypes.string.isRequired
};

export default PieChartCustom;